/**
 * RoomRoster — one team's roster drawn as position cards, ROOM-relative.
 *
 * 🔴 TWO PAGES DRAW A ROOM AND THEY MUST DRAW IT THE SAME WAY (round 6, Aug 30
 * 2026). The Sleeper league report renders every team in a fetched auction;
 * the team tab renders a room the user typed in one team at a time. Those are
 * two different sources of the same object, so the rendering lives here and
 * both import it. A second copy would look identical the day it shipped.
 *
 * THE YARDSTICK IS THE ROOM, on both pages. The badge is the room rank word -
 * strong / solid / thin against the other teams in this auction - and carries
 * no percentile, because a corpus percentile beside it would give one report
 * two yardsticks and no way to tell which claim was which. The team tab's
 * SINGLE-team report keeps corpus percentiles; a lone team has no room to be
 * ranked in, which is exactly why those two readings differ.
 *
 * A group the room does not rank - FLEX, SUPERFLEX, K, DEF and every bench card
 * - shows its players, its points and a dash where the badge would be, with a
 * note saying which kind of "not ranked" it is. Never a word the data cannot
 * back.
 */

import {
  GRADE_POSITIONS,
  KD_POSITIONS,
  playerKey,
  roomGroups,
} from "../../utils/rateProjection.js";
import { posRankOf } from "../../utils/rateTeam.js";
import { theme } from "../../data/theme.js";
import { C, SZ } from "../Submit/submitStyles.js";
import GroupCard from "./GroupCard.jsx";

/**
 * Positional strength words map onto the same palette the letter grades use.
 * Never danger: "thin" is a scouting note about a roster, not a failure, and
 * the copy stays fun. A position with nothing matched is muted, not thin.
 */
export const POS_GRADE_COLOR = {
  strong: C.status.success,
  solid: C.brand.accent,
  thin: C.status.warning,
};

/**
 * What a card reads off when the room could not be projected at all. Nothing to
 * look up and nothing to rank, so every row draws its dashes and every badge
 * draws its own.
 */
export const EMPTY_PROJ = { perPlayer: {}, positionGrades: {} };

/** The heading above each half of the roster. */
const rosterHeading = {
  fontSize: SZ.small,
  fontWeight: 800,
  margin: "10px 0 6px",
  color: C.text.secondary,
};

/**
 * The four position words for one team, ranked inside its own room.
 *
 * Shared with the roster cards below on purpose: both read positionGrades, so a
 * chip and the badge on the card underneath it cannot say different things
 * about the same position.
 */
export function RoomPositionChips({ grades }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      {GRADE_POSITIONS.map((pos) => {
        const g = (grades || {})[pos];
        const word = g && g.grade ? g.grade : "no projection";
        const color = (g && POS_GRADE_COLOR[g.grade]) || C.text.faint;
        return (
          <span
            key={pos}
            style={{
              fontSize: SZ.micro,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: theme.radius.lg,
              border: `1px solid ${color}`,
              color,
            }}
          >
            {pos} {word}
          </span>
        );
      })}
    </div>
  );
}

/**
 * One position group, read the room's way.
 *
 * 🔴 THE FOUR RANKED GROUPS ARE THE FOUR THE PAGE ALREADY RANKS.
 * GRADE_POSITIONS is what assignPositionGrades computes terciles over, and the
 * chips above the roster read the same object, so a card and the chip above it
 * can never say different things about the same position. FLEX and SUPERFLEX
 * have no room cell and are NOT given one here: inventing a room statistic on
 * the way to drawing a card would be a new number nobody measured.
 */
export function RoomGroupCard({ group, proj, ranks, teamCount, hasPrices, wide }) {
  const graded =
    group.kind === "starters" && GRADE_POSITIONS.includes(group.key)
      ? (proj.positionGrades || {})[group.key]
      : null;
  const word = graded && graded.grade ? graded.grade : null;
  const note = word
    ? `ranked in this ${teamCount}-team room: starters plus one depth piece`
    : group.kind === "bench"
      ? "bench, not ranked in this room"
      : KD_POSITIONS.includes(group.key)
        ? "standard scoring, not ranked in this room"
        : graded
          ? "no projection, so no room ranking"
          : "lineup slot, not ranked in this room";
  return (
    <GroupCard
      group={group}
      badge={word}
      badgeColor={(word && POS_GRADE_COLOR[word]) || C.text.faint}
      // The word has to fit a 46px badge box, where a letter grade did not have
      // to. Nothing else about the badge changes.
      badgeSize={word ? 11 : 18}
      note={note}
      hasPrices={hasPrices}
      wide={wide}
      rowInfo={(pick) => ({
        proj: proj.perPlayer[playerKey(pick.player, pick.pos)] || null,
        rank: posRankOf(pick.player, pick.pos, ranks),
      })}
    />
  );
}

/**
 * The whole roster: starters as the lineup filler placed them, then the bench.
 *
 * With no projection at all there is no lineup to fill, so every pick lands in
 * the bench half; the caller passes `projected` false and the split is relabeled
 * a roster rather than calling a whole team "bench".
 *
 * @param picks     the team's graded picks
 * @param proj      projectTeam output plus positionGrades, or null
 * @param ranks     the position board under this league's own scoring
 * @param teamCount how many teams the room ranks against
 */
export default function RoomRoster({ picks, proj, ranks, teamCount, hasPrices = true, wide, testId }) {
  const projected = !!proj;
  const built = roomGroups(picks, proj);
  const groups = projected
    ? built
    : { starters: built.bench.map((g) => ({ ...g, kind: "starters" })), bench: [] };
  const read = proj || EMPTY_PROJ;

  return (
    <div data-testid={testId} style={{ marginTop: 8 }}>
      {groups.starters.length > 0 && (
        <>
          <div style={rosterHeading}>{projected ? "Starters" : "Roster"}</div>
          {groups.starters.map((g) => (
            <RoomGroupCard
              key={`s-${g.key}`}
              group={g}
              proj={read}
              ranks={ranks}
              teamCount={teamCount}
              hasPrices={hasPrices}
              wide={wide}
            />
          ))}
        </>
      )}
      {groups.bench.length > 0 && (
        <>
          <div style={rosterHeading}>Bench</div>
          {groups.bench.map((g) => (
            <RoomGroupCard
              key={`b-${g.key}`}
              group={g}
              proj={read}
              ranks={ranks}
              teamCount={teamCount}
              hasPrices={hasPrices}
              wide={wide}
            />
          ))}
        </>
      )}
    </div>
  );
}
